// ─── The dashboard widget catalog (z8nrz7eqyf) ───────────────────────────────
//
// One registry for every widget a dashboard can hold, so the picker, the grid
// and a saved layout cannot disagree about what exists. Same split as Reports:
// a widget is listed here FIRST and implemented later, and membership of
// `BUILT_WIDGETS` (components/dashboard/builtWidgets.tsx) is what decides
// whether it renders. Nothing here says "built".
//
// Ids are stored in users' saved layouts, so they are permanent: renaming one
// silently drops the widget from every layout that holds it. Add a new id
// instead.
//
// Pure and dependency-free, so it can be unit-tested: the permission lookup is
// passed in by the caller, the same way homeLayouts takes `canAnyRoute`.

export type WidgetSize = 'small' | 'wide' | 'full'

export const WIDGET_SIZES: { id: WidgetSize; label: string }[] = [
  { id: 'small', label: 'Small' },
  { id: 'wide', label: 'Wide' },
  { id: 'full', label: 'Full width' },
]

export function isWidgetSize(value: unknown): value is WidgetSize {
  return WIDGET_SIZES.some((s) => s.id === value)
}

export type DashboardModule = {
  /** Matches the Home tile id where the module has one. */
  id: string
  label: string
  /** Where "Open <module>" goes. */
  to: string
  /**
   * The permission keys that reveal the module's widgets, any-of. Omitted
   * means the route's own nav permissions (`navPermissionsFor`), so a widget is shown
   * exactly when the page it links to is. Set it only where the route is not
   * in that table but is still guarded.
   */
  permissions?: string[]
}

export type DashboardModuleGroup = {
  id: string
  title: string
  modules: DashboardModule[]
}

/**
 * Every module the dashboard covers, in the four groups the epic (z8nrz7buqm)
 * names. Adding a module here without a widget fails the coverage test.
 */
export const DASHBOARD_MODULE_GROUPS: DashboardModuleGroup[] = [
  {
    id: 'transplant',
    title: 'Transplant & Patients',
    modules: [
      { id: 'transplant', label: 'Transplant', to: '/procedures' },
      { id: 'upcoming-procedure', label: 'Upcoming Procedure', to: '/procedures/calendar' },
      { id: 'patients', label: 'Patients', to: '/patients' },
      { id: 'collection', label: 'Collection / Forms', to: '/forms' },
    ],
  },
  {
    id: 'process',
    title: 'Process',
    modules: [
      { id: 'processing', label: 'Processing', to: '/submissions' },
      // /mes carries no route permission, so the default would show it to
      // everyone; its runs are read under procedures.read.
      { id: 'mes', label: 'MES', to: '/mes', permissions: ['procedures.read'] },
      { id: 'kit', label: 'Kit', to: '/kits' },
      // The Cleaning page is the Equipment page for rooms (z8nrz7ewt3), so it
      // is gated the way Equipment is: by the feature, not a permission.
      { id: 'clean', label: 'Cleaning', to: '/cleaning' },
      { id: 'pick-lists', label: 'Pick lists', to: '/inventory/pick-lists' },
    ],
  },
  {
    id: 'inventory',
    title: 'Inventory & Storage',
    modules: [
      { id: 'inventory', label: 'Inventory', to: '/inventory' },
      { id: 'pending-inventory', label: 'Pending Inventory', to: '/inventory?tab=pending' },
      { id: 'storage', label: 'Storage', to: '/inventory?tab=storage' },
      { id: 'products', label: 'Products', to: '/products' },
      // Shipping has no page of its own: a shipment is made and read from a
      // product (ShipProductDialog), so its widgets open Products.
      { id: 'shipping', label: 'Shipping', to: '/products' },
    ],
  },
  {
    id: 'quality',
    title: 'Equipment & Quality',
    modules: [
      { id: 'equipment', label: 'Equipment', to: '/equipment' },
      { id: 'resource', label: 'Resource', to: '/resources' },
      { id: 'capa', label: 'CAPA', to: '/capa' },
      { id: 'notifications', label: 'Notifications', to: '/notifications' },
      // Not in the nav table, so the default would show audit widgets to
      // everyone. audit.read, not any trail key: the widgets read dashboard
      // endpoints that need it, while /audit itself opens to any trail key.
      { id: 'audit', label: 'Audit', to: '/audit', permissions: ['audit.read'] },
      { id: 'integration', label: 'Integration', to: '/integrations' },
    ],
  },
]

export type DashboardWidgetDef = {
  /** Permanent: stored in saved layouts. */
  id: string
  /** A `DashboardModule.id`. */
  module: string
  title: string
  description: string
  defaultSize: WidgetSize
}

/**
 * Every widget, grouped by the ticket that builds it. Order here is the order
 * the picker lists them in, within a module.
 */
export const DASHBOARD_WIDGETS: DashboardWidgetDef[] = [
  // D2b — Transplant, Patients & Forms (z8nrz7eqyj)
  { id: 'transplant.in-progress', module: 'transplant', title: 'Transplants in progress', description: 'Transplant procedures started and not yet completed.', defaultSize: 'small' },
  { id: 'transplant.completed', module: 'transplant', title: 'Completed in period', description: 'Procedures completed in the selected period, by type.', defaultSize: 'small' },
  { id: 'transplant.pipeline', module: 'transplant', title: 'Pipeline by stage', description: 'Active transplant procedures counted by their current stage.', defaultSize: 'full' },
  { id: 'transplant.throughput', module: 'transplant', title: 'Started vs completed', description: 'Procedures started and completed each day, across every type.', defaultSize: 'wide' },
  { id: 'transplant.by-type', module: 'transplant', title: 'Procedures by type', description: 'Procedures started in the period, split by procedure type.', defaultSize: 'wide' },
  { id: 'transplant.at-risk', module: 'transplant', title: 'At-risk procedures', description: 'Open procedures past their due date or due within 48 hours.', defaultSize: 'wide' },
  { id: 'upcoming-procedure.next-days', module: 'upcoming-procedure', title: 'Upcoming procedures', description: 'Procedures scheduled to start in the coming days.', defaultSize: 'small' },
  { id: 'patients.new', module: 'patients', title: 'New patients', description: 'Patients registered in the selected period.', defaultSize: 'small' },
  { id: 'patients.active', module: 'patients', title: 'Active patients', description: 'Patients with a procedure in progress.', defaultSize: 'small' },
  { id: 'collection.awaiting-review', module: 'collection', title: 'Forms awaiting review', description: 'Submitted forms waiting for a review or signature.', defaultSize: 'small' },

  // D3b — Processing, MES, Kits, Cleaning & Pick lists (z8nrz7eqyn)
  { id: 'processing.steps-in-progress', module: 'processing', title: 'Process steps in progress', description: 'Process steps started and not yet ended.', defaultSize: 'small' },
  { id: 'processing.overdue-steps', module: 'processing', title: 'Process steps late to start', description: 'Assigned steps whose planned start has passed.', defaultSize: 'wide' },
  { id: 'mes.runs-by-status', module: 'mes', title: 'MES runs by status', description: 'MES procedures by status in the selected period.', defaultSize: 'wide' },
  { id: 'kit.builds-by-status', module: 'kit', title: 'Kit builds by status', description: 'Kit builds by status in the selected period.', defaultSize: 'wide' },
  { id: 'clean.due', module: 'clean', title: 'Cleaning due', description: 'Cleaning procedures due or in progress.', defaultSize: 'small' },
  { id: 'pick-lists.open', module: 'pick-lists', title: 'Open pick lists', description: 'Pick lists not yet fully picked.', defaultSize: 'small' },
  { id: 'pick-lists.shortages', module: 'pick-lists', title: 'Pick-list shortages', description: 'Open pick-list lines that cannot be filled from stock.', defaultSize: 'wide' },

  // D4b — Inventory, Storage, Products & Shipping (z8nrz7eqyq)
  { id: 'inventory.below-minimum', module: 'inventory', title: 'Stock below minimum', description: 'Materials out of stock or under their minimum level (report 2.1).', defaultSize: 'small' },
  { id: 'inventory.stock-by-category', module: 'inventory', title: 'Stock level by category', description: 'Materials out of stock, below minimum and healthy, per category.', defaultSize: 'wide' },
  { id: 'inventory.expiring', module: 'inventory', title: 'Shelf life of stock', description: 'Lots in stock by how soon they expire: expired, within 30 days, 31–90 days, later, no date.', defaultSize: 'wide' },
  { id: 'inventory.consumption', module: 'inventory', title: 'Consumption', description: 'Quantity consumed by procedures each day in the period (report 2.2).', defaultSize: 'wide' },
  { id: 'inventory.top-consumed', module: 'inventory', title: 'Most consumed materials', description: 'The materials procedures used most in the period.', defaultSize: 'wide' },
  { id: 'inventory.lots-on-hand', module: 'inventory', title: 'Lots on hand', description: 'Lots holding stock and the total quantity on hand, with receipts per day.', defaultSize: 'small' },
  { id: 'pending-inventory.count', module: 'pending-inventory', title: 'Pending inventory', description: 'Lots in stock still held in quarantine or their release workflow.', defaultSize: 'small' },
  { id: 'storage.capacity', module: 'storage', title: 'Storage capacity', description: 'Storage positions used and free across every unit.', defaultSize: 'small' },
  { id: 'storage.capacity-by-type', module: 'storage', title: 'Capacity by temperature', description: 'Positions used and free per temperature family (−80 °C, LN2, 2–8 °C…).', defaultSize: 'wide' },
  { id: 'storage.units', module: 'storage', title: 'Fullest storage units', description: 'Each storage unit by how full it is, fullest first.', defaultSize: 'wide' },
  { id: 'products.by-status', module: 'products', title: 'Where products are', description: 'Every product: in storage, shipped, or not yet placed.', defaultSize: 'wide' },
  { id: 'products.released', module: 'products', title: 'Products made', description: 'Products created in the period, with the daily trend.', defaultSize: 'small' },
  { id: 'products.by-catalog', module: 'products', title: 'Products by type', description: 'Products created in the period, by catalog entry.', defaultSize: 'wide' },
  { id: 'shipping.by-status', module: 'shipping', title: 'Shipments', description: 'Shipments sent and products shipped in the period, per day.', defaultSize: 'wide' },
  { id: 'shipping.by-partner', module: 'shipping', title: 'Shipments by partner', description: 'Where shipments went in the period, and by which courier.', defaultSize: 'wide' },

  // D5b — Equipment, Resources, CAPA, Notifications, Audit & Integrations (z8nrz7eqyt)
  { id: 'equipment.maintenance-due', module: 'equipment', title: 'Maintenance due', description: 'Maintenance overdue or due in the next 7 days.', defaultSize: 'small' },
  { id: 'equipment.maintenance-list', module: 'equipment', title: 'Upcoming maintenance', description: 'The next maintenance tasks by due date, overdue first.', defaultSize: 'wide' },
  { id: 'equipment.maintenance-done', module: 'equipment', title: 'Maintenance completed', description: 'Maintenance completed in the period, and how much was on time.', defaultSize: 'wide' },
  { id: 'equipment.by-status', module: 'equipment', title: 'Equipment availability', description: 'Operational and out-of-service equipment, as an availability rate.', defaultSize: 'small' },
  { id: 'equipment.by-type', module: 'equipment', title: 'Equipment by type', description: 'The equipment fleet by type.', defaultSize: 'wide' },
  { id: 'equipment.reservations-today', module: 'equipment', title: "Today's reservations", description: 'Equipment reservations covering today.', defaultSize: 'small' },
  { id: 'resource.availability', module: 'resource', title: 'Resource availability', description: 'Operational equipment booked and free today.', defaultSize: 'wide' },
  { id: 'capa.open-vs-closed', module: 'capa', title: 'CAPA status', description: 'Open, started, closed and overdue CAPAs, as on the CAPA page.', defaultSize: 'wide' },
  { id: 'capa.overdue', module: 'capa', title: 'Overdue CAPAs', description: 'Open CAPAs past their run’s target date.', defaultSize: 'small' },
  { id: 'capa.trend', module: 'capa', title: 'CAPAs opened vs closed', description: 'CAPAs raised and closed each day in the period.', defaultSize: 'wide' },
  { id: 'capa.ageing', module: 'capa', title: 'Open CAPA ageing', description: 'Open CAPAs by how long ago they were raised.', defaultSize: 'wide' },
  { id: 'capa.time-to-close', module: 'capa', title: 'Time to close', description: 'Average days from raising a CAPA to closing it, for those closed in the period.', defaultSize: 'small' },
  { id: 'notifications.alerts', module: 'notifications', title: 'Alerts', description: 'Your unread notifications, newest first.', defaultSize: 'wide' },
  { id: 'notifications.by-category', module: 'notifications', title: 'Notifications by type', description: 'Your notifications in the period, by what they are about.', defaultSize: 'wide' },
  { id: 'audit.recent', module: 'audit', title: 'Recent audit activity', description: 'The latest changes recorded in the audit trail.', defaultSize: 'wide' },
  { id: 'audit.activity', module: 'audit', title: 'Audit activity', description: 'Audit trail entries per day, by kind of change and by user.', defaultSize: 'wide' },
  { id: 'integration.failures', module: 'integration', title: 'Integration health', description: 'Inbound messages received and failed, and the success rate.', defaultSize: 'wide' },
  { id: 'integration.failure-list', module: 'integration', title: 'Integration failures', description: 'The latest inbound messages that failed or were invalid.', defaultSize: 'wide' },
]

const WIDGET_BY_ID = new Map(DASHBOARD_WIDGETS.map((w) => [w.id, w]))
const MODULES = DASHBOARD_MODULE_GROUPS.flatMap((g) => g.modules.map((m) => ({ ...m, group: g.id })))
const MODULE_BY_ID = new Map(MODULES.map((m) => [m.id, m]))

export function dashboardWidget(id: string): DashboardWidgetDef | undefined {
  return WIDGET_BY_ID.get(id)
}

export function dashboardModule(id: string): (DashboardModule & { group: string }) | undefined {
  return MODULE_BY_ID.get(id)
}

export type WidgetAccess = {
  /** `usePermission().canAny`. */
  canAny: (keys: readonly string[]) => boolean
  /** `navPermissionsFor` from lib/permissions. */
  navPermissionsFor: (to: string) => string[]
}

/** The permission keys that reveal a module's widgets, any-of. [] ⇒ always shown. */
export function modulePermissions(
  module: DashboardModule,
  navPermissionsFor: WidgetAccess['navPermissionsFor'],
): string[] {
  return module.permissions ?? navPermissionsFor(module.to)
}

/**
 * Whether the signed-in user may see a widget at all. Permission HIDES, the
 * same rule as Home tiles. A widget whose module is unknown is never shown.
 */
export function isWidgetAllowed(
  widget: Pick<DashboardWidgetDef, 'module'>,
  access: WidgetAccess,
): boolean {
  const module = MODULE_BY_ID.get(widget.module)
  return module ? access.canAny(modulePermissions(module, access.navPermissionsFor)) : false
}

/** Modules no widget covers. The coverage test holds this at empty. */
export function modulesWithoutWidgets(): string[] {
  const covered = new Set(DASHBOARD_WIDGETS.map((w) => w.module))
  return MODULES.filter((m) => !covered.has(m.id)).map((m) => m.id)
}

// ─── How a widget can be shown ───────────────────────────────────────────────
//
// Each widget offers only the representations its data suits, first one the
// default: a count with a daily history can be a number, a trend or columns;
// a total split into parts a stacked bar, a donut or bars; rows that need
// action a list. Stored on the placement, so it is per widget per dashboard.

export type WidgetView =
  | 'number'
  | 'trend'
  | 'columns'
  | 'lines'
  | 'bars'
  | 'donut'
  | 'stack'
  | 'stages'
  | 'list'
  | 'table'

export const WIDGET_VIEW_LABELS: Record<WidgetView, string> = {
  number: 'Number',
  trend: 'Trend',
  columns: 'Columns',
  lines: 'Lines',
  bars: 'Bars',
  donut: 'Donut',
  stack: 'Stacked bar',
  stages: 'Stages',
  list: 'List',
  table: 'Table',
}

const WIDGET_VIEWS: Record<string, WidgetView[]> = {
  // A count split by type: the parts views, plus the headline number.
  'transplant.in-progress': ['number', 'donut', 'bars', 'stack', 'table'],
  // A count with a daily history: number, trend, columns, a day table.
  'transplant.completed': ['number', 'trend', 'columns', 'table'],
  'transplant.throughput': ['lines', 'trend', 'columns', 'table'],
  'transplant.pipeline': ['stages', 'bars', 'donut', 'stack', 'table'],
  'transplant.by-type': ['bars', 'donut', 'stack', 'table'],
  'transplant.at-risk': ['list', 'number', 'donut', 'bars'],
  'upcoming-procedure.next-days': ['number', 'columns', 'trend', 'table', 'list'],
  'patients.new': ['number', 'trend', 'columns', 'table'],
  'patients.active': ['number', 'donut', 'stack', 'bars'],
  'collection.awaiting-review': ['number', 'trend', 'columns', 'table'],
  'processing.steps-in-progress': ['number', 'donut', 'bars', 'stack', 'table'],
  'processing.overdue-steps': ['list', 'number'],
  'mes.runs-by-status': ['stack', 'donut', 'bars', 'table'],
  'kit.builds-by-status': ['stack', 'donut', 'bars', 'trend', 'columns'],
  'clean.due': ['stack', 'number', 'donut', 'bars', 'table'],
  'pick-lists.open': ['stack', 'donut', 'bars', 'table'],
  'pick-lists.shortages': ['list', 'number', 'bars', 'donut', 'table'],

  'inventory.below-minimum': ['number', 'donut', 'bars', 'stack', 'table'],
  'inventory.stock-by-category': ['bars', 'table'],
  'inventory.expiring': ['bars', 'donut', 'stack', 'table'],
  'inventory.consumption': ['trend', 'columns', 'table'],
  'inventory.top-consumed': ['bars', 'donut', 'table'],
  'inventory.lots-on-hand': ['number', 'trend', 'columns', 'table'],
  'pending-inventory.count': ['number', 'donut', 'stack', 'bars'],
  'storage.capacity': ['number', 'donut', 'stack', 'bars', 'table'],
  'storage.capacity-by-type': ['bars', 'table'],
  'storage.units': ['bars', 'table'],
  'products.by-status': ['donut', 'stack', 'bars', 'table'],
  'products.released': ['number', 'trend', 'columns', 'table'],
  'products.by-catalog': ['bars', 'donut', 'stack', 'table'],
  'shipping.by-status': ['columns', 'trend', 'table', 'number'],
  'shipping.by-partner': ['bars', 'donut', 'table'],

  'equipment.maintenance-due': ['number', 'donut', 'stack', 'bars', 'table'],
  'equipment.maintenance-list': ['list', 'table'],
  'equipment.maintenance-done': ['columns', 'trend', 'table', 'number'],
  'equipment.by-status': ['number', 'donut', 'stack', 'bars'],
  'equipment.by-type': ['bars', 'donut', 'stack', 'table'],
  'equipment.reservations-today': ['number', 'donut', 'stack', 'bars'],
  'resource.availability': ['stack', 'donut', 'bars', 'table', 'number'],
  'capa.open-vs-closed': ['stack', 'bars', 'donut', 'table'],
  'capa.overdue': ['number', 'donut', 'stack', 'bars'],
  'capa.trend': ['lines', 'trend', 'columns', 'table'],
  'capa.ageing': ['bars', 'donut', 'stack', 'table'],
  'capa.time-to-close': ['number'],
  'notifications.alerts': ['list', 'number'],
  'notifications.by-category': ['bars', 'donut', 'stack', 'table'],
  'audit.recent': ['list', 'number'],
  'audit.activity': ['columns', 'trend', 'table', 'bars'],
  'integration.failures': ['lines', 'trend', 'number', 'donut', 'bars'],
  'integration.failure-list': ['list', 'number'],
}

/** The views a widget offers, its default first. Never empty. */
export function widgetViews(id: string): WidgetView[] {
  return WIDGET_VIEWS[id] ?? ['number']
}

/** A stored view if this widget offers it, else its default. */
export function resolveWidgetView(id: string, view: unknown): WidgetView {
  const views = widgetViews(id)
  return views.includes(view as WidgetView) ? (view as WidgetView) : views[0]
}
